import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import api from '../services/api';
import { setColumnConfig, ColumnConfig } from '../utils/dynamicColumns';

export interface FieldDefinition {
  id: number;
  tablename: string;
  fieldname: string;
  displayname: string;
  abbreviation: string | null;
  type: 'string' | 'number' | 'date' | 'datetime' | 'time' | 'boolean' | 'decimal' | 'text' | 'nullable_boolean';
  join_key?: string | null;
  // Raw SQL for a computed field (used instead of source_alias.source_fieldname).
  // A computed column has no single-valued join_key, so pickers key off this to
  // still offer it — mirrors the backend's `fd.sql_expression or fd.join_key`.
  sql_expression?: string | null;
  enum_source?: string | null;
  enum_options?: string[] | null;
  is_displayable: boolean;
  is_filterable: boolean;
  // Starting calculation for the TanTable totals bar, set in Field Manager. Null
  // or absent means the column shows no total until a user picks one; a user's
  // own pick always wins. generateColumnDef stamps this onto meta.aggDefault.
  aggregate_default?: string | null;
  // True when displaying this field can duplicate the pro row (its join is
  // one-to-many). Backend-computed from app.core.row_expanding — the single
  // source that drives the column-picker "(pros may dupe)" warning. See
  // GET /field-definitions.
  expands?: boolean;
  // The same question for a surface that fans the generic `leg` join into one row per
  // matching leg — the Custom Filter. A SECOND flag rather than part of `expands`
  // because the app loads ONE registry for every grid and the dashboards must keep
  // collapsing legs, or their pro-counting badges stop matching their rows. A host ORs
  // this in only while it is actually fanning. Backend-computed from the same
  // app.core.row_expanding module, so it cannot drift from the group-by decision.
  expands_leg_fan?: boolean;
  // Which independent fan this field rides, when it expands. Two expanding columns can
  // share a grid only if these match — `addlcost_lineitem` hangs off `addlcost` (one fan),
  // while Main-invoice lines and Any-invoice lines are separate fans that would
  // Cartesian-product the row. Backend-computed by walking depends_on_join_key, for the
  // same reason `expands` is: SearchResults hardcoded this map and it drifted from the
  // join graph. Absent on a field that cannot expand.
  expanding_branch?: string | null;
  // Bindings this field cannot be offered under, backend-computed from the join graph
  // and the column diff (app/utils/join_resolver.py). A template has no trouble
  // tickets, and the `template` table lacks a few of orderscurrent's columns, so those
  // fields have no answer there — the picker drops them rather than resolving them
  // against the ORDER tables. Absent/empty means available everywhere.
  unavailable_in?: string[];
}

interface FieldDefinitionsContextType {
  fieldDefinitions: FieldDefinition[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const FieldDefinitionsContext = createContext<FieldDefinitionsContextType | undefined>(undefined);

export const useFieldDefinitions = () => {
  const context = useContext(FieldDefinitionsContext);
  if (!context) {
    throw new Error('useFieldDefinitions must be used within a FieldDefinitionsProvider');
  }
  return context;
};

/** Empty, stable fallback — a fresh array would re-fire every consumer's memo. */
const NO_FIELD_DEFINITIONS: FieldDefinition[] = [];

/**
 * Field definitions if a provider is present, an empty list if not.
 *
 * For consumers that only IMPROVE with them — resolving a fieldname to its display name
 * in a tooltip, say. Those should not take the whole page down when the provider is
 * absent, and the provider is not free to mount: it depends on MSAL, so requiring it
 * turns a cosmetic lookup into an auth dependency for every test that renders the host.
 * Anything that cannot function without the definitions should keep using
 * useFieldDefinitions and fail loudly.
 */
export const useOptionalFieldDefinitions = (): FieldDefinition[] =>
  useContext(FieldDefinitionsContext)?.fieldDefinitions ?? NO_FIELD_DEFINITIONS;

interface FieldDefinitionsProviderProps {
  children: ReactNode;
}

export const FieldDefinitionsProvider: React.FC<FieldDefinitionsProviderProps> = ({ children }) => {
  const { accounts } = useMsal();
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchFieldDefinitions = async () => {
    // Don't fetch if user is not logged in
    if (!accounts || accounts.length === 0) {
      setLoading(false);
      return;
    }

    // Abort any previous request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      // Fetch both field definitions and column config in parallel
      const [fieldDefsResponse, columnConfigResponse] = await Promise.all([
        api.get('/field-definitions', { signal: controller.signal }),
        api.get('/dispatch-dashboard/column-config', { signal: controller.signal }).catch((err) => {
          // Ignore abort errors, rethrow others
          if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') throw err;
          // Column config is optional - log but don't fail
          console.warn('[FieldDefinitionsContext] Could not load column config, using defaults:', err);
          return null;
        }),
      ]);

      const defs = fieldDefsResponse.data.data || [];
      setFieldDefinitions(defs);

      // Set column config if successfully loaded
      if (columnConfigResponse?.data) {
        setColumnConfig(columnConfigResponse.data as ColumnConfig);
      }
    } catch (err: any) {
      // Ignore abort errors - expected when we cancel stale requests
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('[FieldDefinitionsContext] Error loading field definitions:', err);
      setError(err as Error);
      setFieldDefinitions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load field definitions when user logs in
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      fetchFieldDefinitions();
    } else {
      setFieldDefinitions([]);
      setLoading(false);
    }

    // Cleanup: abort any in-flight request on unmount or dependency change
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [accounts]);

  const value: FieldDefinitionsContextType = {
    fieldDefinitions,
    loading,
    error,
    refetch: fetchFieldDefinitions,
  };

  return (
    <FieldDefinitionsContext.Provider value={value}>
      {children}
    </FieldDefinitionsContext.Provider>
  );
};
